import { auCentime } from './vat';

/** Taux de l'amortissement dégressif d'un véhicule, admis par l'AFC (en %). */
export const TAUX_DEGRESSIF = 40;

/** Durée par défaut de l'amortissement linéaire d'un véhicule (20 % par an). */
export const DUREE_LINEAIRE = 5;

/** Taux de TVA par défaut des montants d'un véhicule. */
const TAUX_TVA = 8.1;

/** Hors-taxe d'un montant TTC : la TVA d'un véhicule de l'entreprise se récupère. */
const ht = (ttc, taux) =>
  (+ttc || 0) / (1 + (taux == null || taux === '' ? TAUX_TVA : +taux) / 100);

/** Rang d'un mois (année × 12 + mois), d'une date ISO ou suisse. */
function rangDuMois(date) {
  const s = String(date || '');
  let m = s.match(/^(\d{4})-(\d{2})/);
  if (m) return +m[1] * 12 + (+m[2] - 1);
  m = s.match(/^\d{1,2}\.(\d{1,2})\.(\d{4})/);
  return m ? +m[2] * 12 + (+m[1] - 1) : null;
}

/** Nombre de mois de [debut, fin[ qui tombent dans l'année. */
function moisDans(debut, fin, annee) {
  const a = Math.max(debut, annee * 12);
  const b = Math.min(fin, annee * 12 + 12);
  return Math.max(0, b - a);
}

/**
 * Le leasing d'une année.
 *
 * <p>La mensualité se déduit chaque mois du contrat. Le premier versement ne
 * se déduit pas d'un coup : il se répartit sur la durée du contrat, et ce qui
 * n'est pas encore réparti au 31 décembre est un actif (« leasing payé
 * d'avance »). Un contrat arrêté plus tôt déduit le reste le mois de l'arrêt.
 */
function leasingDeLAnnee(v, annee) {
  const debut = rangDuMois(v.dateDebut);
  const duree = Math.max(1, +v.dureeMois || 0);
  if (debut == null) return null;
  const finContrat = debut + duree;
  const arret = rangDuMois(v.dateFin);
  const fin = arret == null ? finContrat : Math.min(finContrat, arret + 1);
  const moisPayes = moisDans(debut, fin, annee);
  const mensualiteHt = ht(v.mensualite, v.tauxTva);
  const initialHt = ht(v.versementInitial, v.tauxTva);
  const parMois = initialHt / duree;
  let reparti = parMois * moisPayes;
  // Arrêté avant terme : ce qui restait à répartir se déduit à l'arrêt.
  if (fin < finContrat && Math.floor((fin - 1) / 12) === annee) {
    reparti += initialHt - parMois * (fin - debut);
  }
  const ecoules = Math.min(Math.max(0, annee * 12 + 12 - debut), fin - debut);
  const avance = fin < finContrat && annee * 12 + 12 >= fin ? 0 : initialHt - parMois * ecoules;
  const tvaInitiale = Math.floor(debut / 12) === annee ? (+v.versementInitial || 0) - initialHt : 0;
  return {
    mensualites: auCentime(mensualiteHt * moisPayes),
    versementReparti: auCentime(reparti),
    amortissement: 0,
    moisPayes,
    tvaRecuperable: auCentime(((+v.mensualite || 0) - mensualiteHt) * moisPayes + tvaInitiale),
    valeurBilan: auCentime(Math.max(0, avance)),
  };
}

/**
 * Le tableau d'amortissement d'un véhicule acheté, année par année.
 *
 * <p>La première année ne compte que les mois depuis l'achat (prorata
 * temporis). Linéaire : le prix hors TVA divisé par la durée. Dégressif :
 * 40 % de la valeur restante chaque année. Vendu, le véhicule s'amortit
 * jusqu'au mois de la vente, puis sort du bilan.
 *
 * @param {object} v fiche du véhicule
 * @returns {Array<{annee: number, debut: number, amortissement: number, fin: number}>}
 */
export function tableauAmortissement(v) {
  const debut = rangDuMois(v?.dateDebut);
  if (!v || v.mode !== 'ACHAT' || debut == null) return [];
  const vente = rangDuMois(v.dateFin);
  const base = ht(v.prixAchat, v.tauxTva);
  const degressif = v.methodeAmortissement === 'DEGRESSIF';
  const annuel = base / Math.max(1, +v.dureeAmortissement || DUREE_LINEAIRE);
  const lignes = [];
  let valeur = auCentime(base);
  for (let annee = Math.floor(debut / 12); valeur > 0 && lignes.length < 40; annee += 1) {
    const premier = Math.max(debut, annee * 12);
    const dernier = vente != null && Math.floor(vente / 12) === annee ? vente + 1 : annee * 12 + 12;
    const prorata = Math.max(0, dernier - premier) / 12;
    const brut = degressif ? (valeur * TAUX_DEGRESSIF) / 100 : annuel;
    let amortissement = auCentime(Math.min(valeur, brut * prorata));
    // Le dégressif ne finit jamais : sous un franc de reste, il est amorti
    // d'un coup. Le tableau courait jusqu'à quarante ans de lignes à 0.00.
    if (degressif && valeur - amortissement < 1) amortissement = valeur;
    const fin = auCentime(valeur - amortissement);
    lignes.push({ annee, debut: valeur, amortissement, fin });
    valeur = fin;
    if (vente != null && Math.floor(vente / 12) === annee) break;
  }
  return lignes;
}

/**
 * Ce qu'un véhicule coûte sur une année, et ce qu'il vaut au bilan.
 *
 * @param {object} v fiche du véhicule
 * @param {number} annee exercice
 * @returns {{charge: number, mensualites: number, versementReparti: number,
 *   amortissement: number, tvaRecuperable: number, valeurBilan: number}}
 *   `charge` est la somme déductible de l'année, hors TVA ; `valeurBilan` la
 *   valeur au 31 décembre (leasing payé d'avance, ou valeur après amortissement)
 */
export function chargeVehicule(v, annee) {
  const vide = {
    charge: 0,
    mensualites: 0,
    versementReparti: 0,
    amortissement: 0,
    tvaRecuperable: 0,
    valeurBilan: 0,
  };
  if (!v) return vide;
  if (v.mode === 'LEASING') {
    const l = leasingDeLAnnee(v, annee);
    return l ? { ...l, charge: auCentime(l.mensualites + l.versementReparti) } : vide;
  }
  const lignes = tableauAmortissement(v);
  const ligne = lignes.find((l) => l.annee === annee);
  const debut = rangDuMois(v.dateDebut);
  const vendu = rangDuMois(v.dateFin);
  const derniere = lignes[lignes.length - 1];
  let valeurBilan = 0;
  if (ligne) valeurBilan = vendu != null && Math.floor(vendu / 12) === annee ? 0 : ligne.fin;
  else if (derniere && annee > derniere.annee && vendu == null) valeurBilan = derniere.fin;
  const achatCetteAnnee = debut != null && Math.floor(debut / 12) === annee;
  return {
    ...vide,
    amortissement: ligne ? ligne.amortissement : 0,
    charge: ligne ? ligne.amortissement : 0,
    tvaRecuperable: achatCetteAnnee
      ? auCentime((+v.prixAchat || 0) - ht(v.prixAchat, v.tauxTva))
      : 0,
    valeurBilan,
  };
}

/**
 * Les véhicules de l'entreprise sur une année : leur charge, leur TVA et ce
 * qu'ils valent au bilan.
 *
 * @param {object[]} vehicules fiches
 * @param {number} annee exercice
 * @returns {{charge: number, tvaRecuperable: number, achats: number,
 *   leasingAvance: number, avecLeasing: boolean}}
 */
export function vehiculesDeLAnnee(vehicules, annee) {
  return (vehicules || []).reduce(
    (t, v) => {
      const c = chargeVehicule(v, annee);
      return {
        charge: auCentime(t.charge + c.charge),
        tvaRecuperable: auCentime(t.tvaRecuperable + c.tvaRecuperable),
        achats: auCentime(t.achats + (v.mode === 'ACHAT' ? c.valeurBilan : 0)),
        leasingAvance: auCentime(t.leasingAvance + (v.mode === 'LEASING' ? c.valeurBilan : 0)),
        avecLeasing: t.avecLeasing || v.mode === 'LEASING',
      };
    },
    { charge: 0, tvaRecuperable: 0, achats: 0, leasingAvance: 0, avecLeasing: false },
  );
}
